<template>
  <div>
    <b-row class="mb-3">
      <b-col md="12" v-if="!isFormValid">
        <b-alert show dismissible>This collective goal is currently inactive. Please fill all the fields below.</b-alert>
      </b-col>
      <b-col md="6">
        <div class="mb-2">
          <h3 class="mb-0">Preview</h3>
          <span class="text-muted">The value displayed is randomly generated.</span>
        </div>
        <collective-goal :template="template" :isPreview="true" class="mb-3" />
      </b-col>
      <b-col md="6">
        <advanced-form
          :hideFormControl="true"
          :form="goalFormFields"
          v-bind="goalForm"
          v-model="goalForm"
          :disabled="viewOnly"
        />
      </b-col>
      <b-col md="12">
        <advanced-form
          :hideFormControl="true"
          :form="formFields"
          v-bind="form"
          v-model="form"
          :disabled="viewOnly"
        />
      </b-col>
    </b-row>
    <!-- Bottom Buttons & Actions -->
    <div v-if="!viewOnly" class="d-flex justify-content-between align-items-center">
      <div class="flex-fill"/>
      <div class="d-flex justify-content-end align-items-center">
        <div class="flex-fill"></div>
        <b-btn @click="input()" variant="primary" class="m-2">Save</b-btn>
      </div>
    </div>
  </div>
</template>

<script>
import CollectiveGoal from '@/components/dashboard/collective-goal';
import UpdateScoreSystem from '@/components/campaign-admin/update-score-system';
import AdvancedForm from '../AdvancedForm';

const eventMap = {
  SHARES: [
    'JOB_SHARED',
    'REFERRAL_SUGGESTED',
    'EVENT_REFERRAL_CREATED',
    'JOB_BROWSE_LINKEDIN',
    'JOB_SCHEDULE_SHARE_FIRST',
    'CONTENT_SHARED',
    'CONTENT_SCHEDULE_SHARE_FIRST'
  ],
  VISITS: [
    'SHARE_UNIQUE_VISITED',
    'CONTENT_SHARE_UNIQUE_VISITED',
  ],
  FIRST_VISITS: [
    'SHARE_FIRST_VISITED',
    'CONTENT_SHARE_FIRST_VISITED',
  ],
  APPLICATIONS: [
    'APPLICATION_CREATED',
  ],
  INTERVIEWS: [
    'APPLICATION_INTERVIEW',
  ],
};

export default {
  components: {
    CollectiveGoal,
    UpdateScoreSystem,
    AdvancedForm
  },
  props: {
    value: {
      type: Object,
    },
    viewOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: null,
      goalForm: null,
    };
  },
  created() {
    const { value } = this;
    const { collectiveGoal } = value || {};
    console.log({ collectiveGoal });
    this.form = Object.keys(collectiveGoal || {}).reduce((r, key) => {
      if ([ 'icon', 'headline', 'goal', 'description' ].includes(key)) {
        r[key] = collectiveGoal[key];
        return r;
      }
      if (key === 'display') {
        r['display.text'] = collectiveGoal[key].text;
        return r;
      }
      return r;
    }, {});
    console.log({ form: this.form });
    this.goalForm = Object.keys(collectiveGoal || {}).reduce((r, key) => {
      if (key === 'conversion') {
        r[key] = collectiveGoal[key];
        return r;
      }
      if (key === 'display') {
        r['display.firstLine'] = collectiveGoal[key].firstLine;
        r['display.secondLine'] = collectiveGoal[key].secondLine;
        return r;
      }
      if (key === 'eventNames') {
        r.events = Object.keys(eventMap).find(k => eventMap[k].join(',') === collectiveGoal[key].join(','));
        return r;
      }
      return r;
    }, {});
    console.log({ goalForm: this.goalForm });
  },
  computed: {
    formFields() {
      return {
        icon: {
          label: 'Icon',
          type: 'STRING',
          helpText: 'To show Emojis, hold ⌃⌘Space on Mac, and Windows Logo + . (period) on Windows',
        },
        headline: {
          label: 'Headline',
          type: 'STRING',
          placeholder: 'Work for a greener planet together',
          helpText: 'This is the headline that will be shown on the top of collective goal component',
        },
        'display.text': {
          label: 'Text',
          type: 'STRING',
          placeholder: 'Every time you share, we plant a tree',
          helpText: 'Short explanation of the collective goal',
        },
        goal: {
          label: 'Goal',
          type: 'NUMBER',
          placeholder: '350',
          helpText: 'This is the total goal for the collective goal',
        },
        description: {
          label: 'Learn More Text',
          type: 'TEXTAREA',
          helpText: 'Longer explanation of the collective goal',
        },
      };
    },
    goalFormFields() {
      return {
        'display.firstLine': {
          label: 'One...',
          placeholder: 'Tree',
          type: 'STRING',
        },
        'display.secondLine': {
          label: 'Is...',
          placeholder: 'Planted',
          type: 'STRING',
        },
        conversion: {
          label: 'With every...',
          placeholder: '4',
          type: 'NUMBER',
        },
        events: {
          label: 'Time(s)...',
          type: 'SELECT',
          choices: {
            SHARES: 'A job or piece of content is shared',
            VISITS: 'A job or piece of content is viewed',
            FIRST_VISITS: 'A job or piece of content is viewed for the first time',
            APPLICATIONS: 'An application is submitted',
            INTERVIEWS: 'An interview is scheduled',
          }
        },
      };
    },
    template() {
      const { form, goalForm } = this;
      const merged = { ...(form || {}), ...(goalForm || {}) };
      return Object.keys(merged).reduce((r, key) => {
        if (key === 'display.text') {
          r.display = { ...(r.display || {}), text: merged[key] };
          return r;
        }
        if (key === 'display.firstLine') {
          r.display = { ...(r.display || {}), firstLine: merged[key] };
          return r;
        }
        if (key === 'display.secondLine') {
          r.display = { ...(r.display || {}), secondLine: merged[key] };
          return r;
        }
        if (key === 'events') {
          r.eventNames = eventMap[merged[key]] ? eventMap[merged[key]] : null;
          return r;
        }
        if (key === 'conversion' || key === 'goal') {
          r[key] = typeof merged[key] === 'string' ? parseFloat(merged[key]) : merged[key];
          return r;
        }
        r[key] = merged[key];
        return r;
      }, {});
    },
    isFormValid() {
      const { template } = this;
      return !!(template && template.description && template.headline && template.icon && template.goal);
    },
  },
  methods: {
    input() {
      const { template } = this;
      console.log('TEMPLATE IS: ', template);
      this.$emit('input', { collectiveGoal: template });
    },
  },
};
</script>

<style lang="scss">
.referral-jam-event-settings {
  .event-logo {
    max-height: 30px;
    max-width: 200px;
  }
}
.form-label {
  font-size: 1rem;
}
.unit-label {
  font-size: 0.9rem;
}
.help-text {
  font-size: 0.75rem;
}
.hidden {
  visibility: hidden;
}
.delete-styling:hover {
  font-weight: bold;
  color: red;
}
.add-styling:hover {
  font-weight: bold;
  color: green;
}
</style>


// WEBPACK FOOTER //
// src/components/campaign-admin/update-simplified-collective-goal.vue